import {
  useCallback, useEffect, useState,
} from 'react';
import PropTypes from 'prop-types';
import { codeOf } from '../../game/shortcuts';

/**
 * Record an NPC's patrol route by walking it.
 *
 * WHY WALK IT RATHER THAN TYPE IT. A route captured from where a character
 * actually stood is walkable by construction, and correctly aligned with the
 * foot collision box. The first seeded route in this project was typed, and its
 * opening waypoint landed inside a wall — the NPC spawned nudged clear so it
 * *looked* right, while its patrol ran entirely on the stuck-timeout fallback.
 * That failure is silent, which is the worst kind.
 *
 * KEYBOARD, NOT BUTTONS, for dropping points. Reaching for the mouse means
 * letting go of the movement keys, and the whole activity is "walk somewhere,
 * mark it, walk on". The buttons exist for the things you do while standing
 * still.
 *
 * React state is right here for the same reason it is right in ChatPanel: this
 * is occasional, DOM-driving state. The rule that keeps entities out of React
 * is about the 20Hz tick, not about everything.
 */

/**
 * Neither is in KEY_MAP, so neither steals a movement key.
 *
 * Declared in the shortcuts registry, which is also what puts them in the
 * legend — so an admin key cannot exist without being documented.
 */
const DROP_KEY = codeOf('dropWaypoint');
const UNDO_KEY = codeOf('undoWaypoint');

/**
 * Typing must never drop a waypoint.
 *
 * Mirrors the guard in Input.js: the name field is a real text input, and
 * without this every "r" typed into it also marks the floor.
 */
const isTyping = () => {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
};

function NpcRecorder({ getFootPosition, mapName, onSave }) {
  // COLLAPSED BY DEFAULT. This is a multi-row panel with a waypoint list, and
  // it is only wanted while authoring — overlaying it permanently would cover a
  // chunk of the world for every admin who is just playing.
  const [open, setOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [waypoints, setWaypoints] = useState([]);
  const [name, setName] = useState('');
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  const drop = useCallback(() => {
    const at = getFootPosition();
    if (!at) {
      setStatus({ error: 'No position yet — wait for the world to load.' });
      return;
    }
    setWaypoints((prior) => [...prior, at]);
    setStatus(null);
  }, [getFootPosition]);

  useEffect(() => {
    if (!recording) return undefined;

    const onKeyDown = (event) => {
      if (isTyping()) return;
      if (event.code === DROP_KEY) drop();
      if (event.code === UNDO_KEY) setWaypoints((prior) => prior.slice(0, -1));
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [recording, drop]);

  const reset = () => {
    setRecording(false);
    setWaypoints([]);
    setName('');
  };

  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      await onSave({ name: name.trim(), mapName, waypoints });
      // SAYS "DRAFT" EXPLICITLY. Everything recorded here starts unpublished,
      // so the character is walking and no player can see them — which, without
      // being told, reads exactly like the save having silently failed.
      setStatus({ ok: `"${name.trim()}" is walking as a draft — only admins can see them. Publish from Admin › NPCs.` });
      reset();
    } catch (err) {
      // Surfaced verbatim: the server names WHICH waypoint is unwalkable, and
      // that is the difference between re-walking one corner and starting over.
      setStatus({ error: err.message || 'Could not save.' });
    } finally {
      setSaving(false);
    }
  };

  const canSave = name.trim().length > 0 && waypoints.length >= 2 && !saving;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="pointer-events-auto rounded bg-gray-900/85 px-2 py-1 text-xs text-gray-300 backdrop-blur-sm hover:bg-gray-800"
      >
        Record an NPC route
      </button>
    );
  }

  return (
    <div className="pointer-events-auto w-full max-w-2xl rounded-lg border border-gray-800 bg-gray-950/90 p-3 text-xs backdrop-blur-sm">
      <div className="flex items-center gap-3">
        <span className="font-medium text-gray-300">NPC route</span>

        {!recording ? (
          <button
            type="button"
            onClick={() => { setRecording(true); setStatus(null); }}
            className="rounded bg-emerald-700 px-2 py-1 text-white hover:bg-emerald-600"
          >
            Start recording
          </button>
        ) : (
          <>
            <span className="text-emerald-400">
              ● recording —
              {' '}
              <kbd className="rounded bg-gray-800 px-1">R</kbd>
              {' '}
              drop point,
              {' '}
              <kbd className="rounded bg-gray-800 px-1">U</kbd>
              {' '}
              undo
            </span>
            <button
              type="button"
              onClick={reset}
              className="rounded bg-gray-700 px-2 py-1 text-gray-200 hover:bg-gray-600"
            >
              Cancel
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => { reset(); setOpen(false); }}
          className="ml-auto rounded px-1 text-gray-500 hover:text-gray-300"
          aria-label="Close the route recorder"
        >
          ×
        </button>
        <span className="text-gray-500">
          {waypoints.length}
          {' '}
          point
          {waypoints.length === 1 ? '' : 's'}
          {' on '}
          {mapName || '—'}
        </span>
      </div>

      {recording && (
        <>
          <div className="mt-2 flex flex-wrap gap-1 text-gray-500">
            {waypoints.length === 0
              ? <span>Walk somewhere and press R.</span>
              : waypoints.map((w, i) => (
                // Index is a stable key here: the list is append-and-undo only,
                // so a given position never moves within it.
                // eslint-disable-next-line react/no-array-index-key
                <span key={i} className="rounded bg-gray-800 px-1">
                  {Math.round(w.x)}
                  ,
                  {Math.round(w.y)}
                </span>
              ))}
          </div>

          <div className="mt-2 flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name them"
              maxLength={16}
              className="rounded border border-gray-700 bg-gray-950 px-2 py-1 text-gray-100"
            />
            <button
              type="button"
              onClick={save}
              disabled={!canSave}
              className="rounded bg-blue-700 px-2 py-1 text-white hover:bg-blue-600 disabled:opacity-40"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {waypoints.length < 2 && (
              <span className="text-gray-600">Two points minimum — a route needs a direction.</span>
            )}
          </div>
        </>
      )}

      {status?.error && <p className="mt-2 text-red-400">{status.error}</p>}
      {status?.ok && <p className="mt-2 text-emerald-400">{status.ok}</p>}
    </div>
  );
}

NpcRecorder.propTypes = {
  /** Reads the local player's FOOT position from the engine. */
  getFootPosition: PropTypes.func.isRequired,
  mapName: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};

NpcRecorder.defaultProps = {
  mapName: null,
};

export default NpcRecorder;
